import { fbt } from 'fbtee'
import { CheckInIcon } from './CheckInIcon'
import { Clock, X } from 'lucide-react'
import { cn } from '../lib/utils'
import { resolveTime } from '../lib/formatDate'
import { useTimeFormat } from '../hooks/useTimeFormat'
import { Slider } from './ui/slider'
import type { PartialRangeEditor } from '../hooks/usePartialRangeEditor'
import { GoingCheckIcon } from './GoingCheckIcon'

/**
 * A person's slice of a set, shaped like the Going `InterestBadge` it stands in
 * for: same pill, but the label names only the edge that isn't the set's own
 * ("until 21:00", "from 20:30") and a hairline along its bottom edge is the
 * whole set with their part lit. A column of these reads at a glance — who's
 * early, who's late — where a column of identical time pills doesn't.
 */
export function PartialRangeTimeline({
  setStartTime,
  duration,
  startMin,
  endMin,
  checkedIn = false,
  className,
}: {
  setStartTime: string
  duration: number
  startMin: number
  endMin: number
  /** Swaps the going check for the "I'm here" double check. */
  checkedIn?: boolean
  className?: string
}) {
  const { formatClock, formatClockRange } = useTimeFormat()
  const from = formatClock(resolveTime(setStartTime, startMin))
  const until = formatClock(resolveTime(setStartTime, endMin))
  const label =
    startMin <= 0
      ? fbt('until ' + fbt.param('time', until), 'Partial set range that starts with the set: when the person leaves')
      : endMin >= duration
        ? fbt('from ' + fbt.param('time', from), 'Partial set range that runs to the end of the set: when the person arrives')
        : formatClockRange(resolveTime(setStartTime, startMin), resolveTime(setStartTime, endMin))
  return (
    <span
      data-testid="partial-range-badge"
      className={cn(
        'relative inline-flex items-center gap-1 overflow-hidden rounded bg-going/10 px-1.5 pt-0.5 pb-1 text-[10px] uppercase tracking-wider tabular-nums text-going shrink-0',
        className,
      )}
    >
      {checkedIn ? (
        <CheckInIcon className="size-3 shrink-0" />
      ) : (
        <GoingCheckIcon className="size-3 shrink-0" />
      )}
      {label}
      <span className="absolute inset-x-0 bottom-0 h-[3px] bg-muted-foreground/40">
        <span
          className="absolute inset-y-0 bg-going"
          style={{ left: `${(startMin / duration) * 100}%`, width: `${((endMin - startMin) / duration) * 100}%` }}
        />
      </span>
    </span>
  )
}

/**
 * The "22:30 – 23:00" pill. Read-only on its own — wrap it in a button when the
 * viewer owns the range and can open the editor.
 */
export function PartialRangeBadge({
  setStartTime,
  startMin,
  endMin,
  checkedIn = false,
  className,
}: {
  setStartTime: string
  startMin: number
  endMin: number
  checkedIn?: boolean
  className?: string
}) {
  const { formatClockRange } = useTimeFormat()
  return (
    <span
      data-testid="partial-range-badge"
      className={cn(
        'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-going bg-going/10 px-1.5 py-0.5 rounded shrink-0',
        className,
      )}
    >
      {checkedIn ? (
        <CheckInIcon className="size-3 shrink-0" />
      ) : (
        <GoingCheckIcon partial className="size-3 shrink-0" />
      )}
      {formatClockRange(resolveTime(setStartTime, startMin), resolveTime(setStartTime, endMin))}
    </span>
  )
}

/** The right-hand affordance: the range timeline once set, otherwise a "Set time" button. */
export function PartialRangeTrigger({
  editor,
  setStartTime,
  duration,
  canEdit,
  checkedIn = false,
}: {
  editor: PartialRangeEditor
  setStartTime: string
  duration: number
  canEdit: boolean
  checkedIn?: boolean
}) {
  const { expanded, hasRange, localRange, toggle } = editor
  if (hasRange || expanded) {
    return (
      <button type="button" onClick={canEdit ? toggle : undefined} className={cn(canEdit && 'cursor-pointer')}>
        {duration > 0 ? (
          <PartialRangeTimeline setStartTime={setStartTime} duration={duration} startMin={localRange[0]} endMin={localRange[1]} checkedIn={checkedIn} />
        ) : (
          <PartialRangeBadge setStartTime={setStartTime} startMin={localRange[0]} endMin={localRange[1]} checkedIn={checkedIn} />
        )}
      </button>
    )
  }
  if (!canEdit) return null
  return (
    <button
      type="button"
      onClick={toggle}
      className="cursor-pointer inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground hover:border-primary/50 hover:text-primary"
    >
      <Clock className="size-3" />
      {fbt('Set time', 'Button label to set a partial totem time range')}
    </button>
  )
}

/** The expanded editor: a two-thumb slider spanning the set, plus a clear action. */
export function PartialRangeSlider({
  editor,
  duration,
  setStartTime,
  setEndTime,
}: {
  editor: PartialRangeEditor
  duration: number
  setStartTime: string
  setEndTime: string
}) {
  const { localRange, setLocalRange, commit, clear, hasRange } = editor
  return (
    <div className="pt-2 pb-1 px-1 space-y-2" data-testid="partial-range-slider">
      {/* Same colors as PartialRangeTimeline, so dragging reads as editing that bar. */}
      <Slider
        className="[&_[data-slot=slider-range]]:bg-going [&_[data-slot=slider-thumb]]:border-going [&_[data-slot=slider-thumb]]:ring-going/50 [&_[data-slot=slider-track]]:bg-muted-foreground/40"
        min={0}
        max={duration}
        step={5}
        value={localRange}
        onValueChange={v => setLocalRange(v as [number, number])}
        onValueCommit={v => commit(v as [number, number])}
      />
      {/* The thumb's touch target extends 12px past its box (`after:-inset-3`),
          which reaches into this row and swallows taps on Clear. Stacking the
          footer above it keeps the button hittable at any thumb position. */}
      <div className="relative z-10 flex items-center justify-between text-xs text-muted-foreground">
        <span>{setStartTime}</span>
        {hasRange && (
          <button type="button" onClick={clear} className="flex items-center gap-0.5 text-destructive cursor-pointer">
            <X className="size-3" />
            {fbt('Clear', 'Button to remove partial totem time range')}
          </button>
        )}
        <span>{setEndTime}</span>
      </div>
    </div>
  )
}
